import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import PeopleOutlined from "@mui/icons-material/PeopleOutlined";
import {
  SmsOutlined,
  LocalShippingOutlined,
  DashboardOutlined,
  FactCheck as FactCheckIcon,
  PaymentsOutlined,
  MedicationOutlined,
  MedicationLiquidOutlined,
  EditOutlined,
  MonitorOutlined,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  SupportAgentOutlined,
  PhoneIphoneOutlined,
  WarningAmberOutlined,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  EventAvailableOutlined,
} from "@mui/icons-material";
import Snackbar from "@mui/material/Snackbar";
import { DataGrid } from "@mui/x-data-grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { dispenseSystemName } from "@/config/dispenseSystems";
import { PHARMACY_HALO_COLORS, QUDA_COLORS, DOCLINK_COLORS, MEDIDROP_COLORS } from "@/config/colors";
import { pharmacyHaloDataGridSx, ADMIN_TOP_CARD_HEIGHT } from "@/utils/adminTableStyles";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { PhoneLink } from "@/utils/PhoneLink";
import ChemistQudaTab from "./ChemistQudaTab";
import ChemistHaloPayTab from "./ChemistHaloPayTab";
import ChemistHaloTextTab from "./ChemistHaloTextTab";
import ChemistDocLinkTab from "./ChemistDocLinkTab";
import ChemistMediDropTab from "./ChemistMediDropTab";
import ChemistWebbitTab from "./ChemistWebbitTab";
import ChemistCallsTab from "./ChemistCallsTab";
import ChemistCommentsTab from "./ChemistCommentsTab";
import ChemistContactNumbers from "./ChemistContactNumbers";
import LocalWarningsModal, { getActiveWarningLabels } from "@/components/modals/admin/LocalWarningsModal";

function fmtAuPhone(raw) {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 10) return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  return raw;
}

function fmtDate(iso) {
  if (!iso) return iso;
  const parts = String(iso).split("-");
  return parts.length === 3
    ? `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`
    : iso;
}

function buildSparkData(perDay) {
  if (!Array.isArray(perDay) || perDay.length === 0) return [];
  return perDay.map((r) => ({ date: r.date, count: Number(r.count ?? 0) }));
}

// ── Calendly Create Booking helpers ──────────────────────────────────────────
const PERTH_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getPerthTodayStr() {
  return new Date(Date.now() + PERTH_OFFSET_MS).toISOString().split("T")[0];
}

function getStripDates(weekOffset) {
  const today = getPerthTodayStr();
  const [y, m, d] = today.split("-").map(Number);
  return Array.from({ length: 7 }, (_, i) => {
    const off = weekOffset * 7 + i;
    return new Date(Date.UTC(y, m - 1, d + off)).toISOString().split("T")[0];
  });
}

function perthDayUtcRange(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const startUtc = new Date(Date.UTC(y, m - 1, d) - PERTH_OFFSET_MS);
  const endUtc   = new Date(Date.UTC(y, m - 1, d + 1) - PERTH_OFFSET_MS);
  return { startUtc: startUtc.toISOString(), endUtc: endUtc.toISOString() };
}

function fmtSlotTimePth(isoUtc) {
  const perthD = new Date(new Date(isoUtc).getTime() + PERTH_OFFSET_MS);
  const h = perthD.getUTCHours();
  const min = perthD.getUTCMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return `${h12}${min > 0 ? ":" + String(min).padStart(2, "0") : ""}${ampm}`;
}

function perthHour(isoUtc) {
  return new Date(new Date(isoUtc).getTime() + PERTH_OFFSET_MS).getUTCHours();
}

function parseDateParts(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return { dayName: DAY_ABBR[dow], dayNum: d, monthName: MON_ABBR[m - 1] };
}

function InfoItem({ label, value, action }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          fontSize: "0.65rem",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
        {action}
      </Box>
    </Box>
  );
}

function EmailButton({ email }) {
  if (!email) return null;
  return (
    <Tooltip title={`Open ${email} in email client`} placement="left">
      <IconButton
        size="small"
        component="a"
        href={`mailto:${email}`}
        aria-label="Open in email client"
        sx={{
          border: `1px solid ${PHARMACY_HALO_COLORS.BLUE}`,
          borderRadius: 1,
          color: PHARMACY_HALO_COLORS.BLUE,
          p: 0.4,
        }}
      >
        <EmailOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function PhoneButton({ phone }) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  return (
    <Tooltip title={`Call ${fmtAuPhone(phone)}`} placement="left">
      <IconButton
        size="small"
        component="a"
        href={`tel:${digits}`}
        aria-label="Call phone number"
        sx={{
          border: `1px solid ${PHARMACY_HALO_COLORS.BLUE}`,
          borderRadius: 1,
          color: PHARMACY_HALO_COLORS.BLUE,
          p: 0.4,
        }}
      >
        <PhoneOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function MiniBarChart({ data, color, label }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color }}>
        {label}
      </Typography>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={data} margin={{ top: 2, right: 4, left: -20, bottom: 18 }}>
          <XAxis
            dataKey="date"
            tick={{ fontSize: 9 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={fmtDate}
            interval="preserveStartEnd"
            angle={-35}
            textAnchor="end"
          />
          <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <ReTooltip
            formatter={(v) => [v, label.split(" ")[0]]}
            labelFormatter={fmtDate}
            contentStyle={{ fontSize: 11 }}
          />
          <Bar dataKey="count" fill={color} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

const staffColumns = [
  {
    field: "Name",
    headerName: "Name",
    flex: 1,
    minWidth: 140,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1 }}>
        {p.value || "—"}
      </Typography>
    ),
  },
  {
    field: "Login",
    headerName: "Login",
    flex: 0.9,
    minWidth: 110,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1, fontFamily: "monospace" }}>
        {p.value || "—"}
      </Typography>
    ),
  },
  {
    field: "Phone",
    headerName: "Phone",
    flex: 0.8,
    minWidth: 110,
    renderCell: (p) => (
      <Typography variant="body2" noWrap sx={{ width: 1 }}>
        {p.value || "—"}
      </Typography>
    ),
  },
  {
    field: "IsActive",
    headerName: "Active",
    width: 72,
    align: "center",
    headerAlign: "center",
    renderCell: (p) =>
      p.value ? (
        <Chip label="Yes" size="small" color="success" variant="outlined" />
      ) : (
        <Chip label="No" size="small" color="default" variant="outlined" />
      ),
  },
];

const CHEMIST_TABS = [
  { label: "Overview",  Icon: DashboardOutlined,       color: PHARMACY_HALO_COLORS.BLUE,  pastel: "#dce3f5",  subKey: null,             metricKey: null,                 metricLabel: null },
  { label: "Quda",      Icon: FactCheckIcon,            color: QUDA_COLORS.TEAL_DARK,      pastel: "#c8f0ee",  subKey: "QudaActive",     metricKey: "TotalOrders30d",     metricLabel: "Transactions 30d" },
  { label: "HaloText",  Icon: SmsOutlined,              color: PHARMACY_HALO_COLORS.BLUE,  pastel: "#fff0c8",  subKey: "HaloTextActive", metricKey: "TotalSms30d",        metricLabel: "SMS 30d" },
  { label: "DocLink",   Icon: MedicationOutlined,       color: DOCLINK_COLORS.DARK_PURPLE, pastel: "#e8daf5",  subKey: "DocLinkActive",  metricKey: "TotalConsults30d",   metricLabel: "Consults 30d" },
  { label: "MediDrop",  Icon: LocalShippingOutlined,    color: MEDIDROP_COLORS.ORANGE,     pastel: "#fde4d8",  subKey: "MediDropActive", metricKey: "TotalDeliveries30d", metricLabel: "Deliveries 30d" },
  { label: "Webbit",    Icon: MedicationLiquidOutlined, color: "#083b4b",                  pastel: "#e8f7c5",  subKey: "WebbitActive",   metricKey: "PacksSupplied30d",   metricLabel: "Packs 30d" },
  { label: "HaloPay",   Icon: PaymentsOutlined,         color: PHARMACY_HALO_COLORS.BLUE,  pastel: "#fff0c8",  subKey: "HaloPayActive",  metricKey: "TotalTransactions30d", metricLabel: "Transactions 30d" },
  { label: "Calls",     Icon: PhoneOutlined,            color: "#E63888",                  pastel: "#FDE8F3",  subKey: null,             metricKey: null,                 metricLabel: null },
  { label: "Terminals", Icon: MonitorOutlined,          color: "#374151",                  pastel: "#f1f5f9",  subKey: null,             metricKey: null,                 metricLabel: null, adminOnly: true },
  { label: "Notes",     Icon: ChatBubbleOutlineIcon,    color: "#7c3aed",                  pastel: "#ede9fe",  subKey: null,             metricKey: null,                 metricLabel: null },
];

const AdminChemistPage = ({ isManager = false }) => {
  const { chemistUUID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const overviewBasePath = isManager ? "/manager-functions/overview" : "/admin-functions/overview";
  const [activeTab, setActiveTab] = useState(() => Number(location.state?.tab ?? 0));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [staffSearch, setStaffSearch] = useState("");
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedGroupID, setSelectedGroupID] = useState("");
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupSaveError, setGroupSaveError] = useState(null);
  const [allowHotfix, setAllowHotfix] = useState(false);
  const [useCherub, setUseCherub] = useState(false);
  const [allowSMSResponse, setAllowSMSResponse] = useState(false);
  const [flagsSaving, setFlagsSaving] = useState(false);
  const [overflowAnchor, setOverflowAnchor] = useState(null);
  const [terminalsData, setTerminalsData] = useState(null);
  const [terminalsLoading, setTerminalsLoading] = useState(false);
  const [splashtopSending, setSplashtopSending] = useState(false);
  const [supportAppLinkSending, setSupportAppLinkSending] = useState(false);
  const [localWarningsOpen, setLocalWarningsOpen] = useState(false);
  const [localWarnings, setLocalWarnings] = useState(null); // cached after first fetch/save
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Calendly booking link modal
  const [calendlyModalOpen, setCalendlyModalOpen]     = useState(false);
  const [calendlyEventTypes, setCalendlyEventTypes]   = useState(null); // null = not fetched
  const [calendlyTypesLoading, setCalendlyTypesLoading] = useState(false);
  const [selectedEventType, setSelectedEventType]     = useState(null);
  const [calendlySending, setCalendlySending]         = useState(false);
  const [greetingName, setGreetingName]               = useState("");

  // Calendly create booking modal
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const [cbStep, setCbStep]                       = useState(1); // 1=event type, 2=date/time
  const [cbSelectedType, setCbSelectedType]       = useState(null);
  const [cbWeekOffset, setCbWeekOffset]           = useState(0);
  const [cbSelectedDate, setCbSelectedDate]       = useState("");
  const [cbAvailableTimes, setCbAvailableTimes]   = useState(null); // null=not fetched
  const [cbTimesLoading, setCbTimesLoading]       = useState(false);
  const [cbSelectedSlot, setCbSelectedSlot]       = useState(null);

  const fetchCbTimes = useCallback(async (eventTypeUri, dateStr) => {
    setCbTimesLoading(true);
    setCbAvailableTimes(null);
    setCbSelectedSlot(null);
    try {
      const { startUtc, endUtc } = perthDayUtcRange(dateStr);
      const res = await phappsRequest(API_PATHS.CALENDLY_AVAILABLE_TIMES, "GET", null, {
        event_type_uri: eventTypeUri,
        start_time: startUtc,
        end_time: endUtc,
      });
      setCbAvailableTimes(res?.available_times || []);
    } catch {
      setCbAvailableTimes([]);
    } finally {
      setCbTimesLoading(false);
    }
  }, []);

  const openCreateBooking = useCallback(() => {
    setCbStep(1);
    setCbSelectedType(null);
    setCbWeekOffset(0);
    setCbSelectedDate("");
    setCbAvailableTimes(null);
    setCbSelectedSlot(null);
    setCreateBookingOpen(true);
    if (!calendlyEventTypes) {
      setCalendlyTypesLoading(true);
      phappsRequest(API_PATHS.CALENDLY_EVENT_TYPES, "GET")
        .then((res) => { setCalendlyEventTypes(res?.event_types || []); })
        .catch(() => { setCalendlyEventTypes([]); })
        .finally(() => setCalendlyTypesLoading(false));
    }
  }, [calendlyEventTypes]);

  // Edit chemist details drawer
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ChemistName: "", Email: "" });
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const openEditDrawer = () => {
    if (!data) return;
    setEditForm({
      ChemistName: data.ChemistName ?? "",
      Email:       data.Email ?? "",
    });
    setEditError(null);
    setEditDrawerOpen(true);
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    setEditError(null);
    try {
      await phappsRequest(API_PATHS.ADMIN_UPDATE_CHEMIST_DETAILS, "PATCH", {
        ChemistUUID:      data.ChemistUUID,
        ChemistName:      editForm.ChemistName.trim(),
        Email:            editForm.Email.trim() || null,
        AllowSMSResponse: allowSMSResponse,
      });
      setEditConfirmOpen(false);
      setEditDrawerOpen(false);
      setSnackbar({ open: true, message: "Chemist details updated successfully", severity: "success" });
      fetchDetail();
    } catch (e) {
      setEditError(e?.response?.data?.message || e?.message || "Failed to save changes");
    } finally {
      setEditSaving(false);
    }
  };

  const fetchDetail = useCallback(async () => {
    if (!chemistUUID) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(
        isManager ? API_PATHS.MANAGER_CHEMIST_PAGE : API_PATHS.ADMIN_CHEMIST_PAGE,
        "GET",
        null,
        { ChemistUUID: chemistUUID }
      );
      setData(res);
      setAllowHotfix(res.AllowHotfix ?? false);
      setUseCherub(res.UseCherub ?? false);
      setAllowSMSResponse(res.AllowSMSResponse ?? false);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load chemist detail"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID]);

  const fetchTerminals = useCallback(async () => {
    if (!chemistUUID || terminalsData) return;
    try {
      setTerminalsLoading(true);
      const res = await phappsRequest(API_PATHS.CHEMIST_TERMINALS, "GET", null, { ChemistUUID: chemistUUID });
      setTerminalsData(res);
    } catch {
      setTerminalsData({ Terminals: [] });
    } finally {
      setTerminalsLoading(false);
    }
  }, [chemistUUID, terminalsData]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Auto-fetch local warnings once ChemistID is known so the indicator shows on load
  useEffect(() => {
    if (!data?.ChemistID || isManager) return;
    let cancelled = false;
    phappsRequest(API_PATHS.LOCAL_WARNINGS, "GET", null, { ChemistID: data.ChemistID })
      .then((res) => { if (!cancelled) setLocalWarnings(res?.warnings ?? null); })
      .catch(() => {}); // silently ignore — indicator just won't show until modal is opened
    return () => { cancelled = true; };
  }, [data?.ChemistID, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const label = CHEMIST_TABS.filter((tab) => !tab.adminOnly || !isManager)[activeTab]?.label;
    if (label === "Terminals") fetchTerminals();
  }, [activeTab, fetchTerminals, isManager]);

  const staffRows = useMemo(() => {
    if (!data?.StaffList) return [];
    let list = data.StaffList;
    if (showActiveOnly) list = list.filter((s) => s.IsActive);
    if (staffSearch.trim()) {
      const q = staffSearch.trim().toLowerCase();
      list = list.filter(
        (s) =>
          String(s.Name ?? "").toLowerCase().includes(q) ||
          String(s.Login ?? "").toLowerCase().includes(q)
      );
    }
    return list.map((s, i) => ({ id: s.StaffID ?? i, ...s }));
  }, [data, showActiveOnly, staffSearch]);

  const totalStaff = data?.StaffList?.length ?? 0;
  const activeStaff = data?.StaffList?.filter((s) => s.IsActive).length ?? 0;

  // Resolve the label of the currently-active tab accounting for manager vs admin filtering
  const visibleTabs = CHEMIST_TABS.filter((tab) => !tab.adminOnly || !isManager);
  const activeTabLabel = visibleTabs[activeTab]?.label ?? "";

  if (loading && !data) {
    return (
      <Box className="flex items-center justify-center min-h-[320px]">
        <Typography color="textSecondary">Loading…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex items-center justify-center min-h-[320px] px-4">
        <Alert severity="warning" sx={{ maxWidth: 560 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data) return null;

  const subs = data.Subscriptions ?? {};
  const ordersData = buildSparkData(data.OrdersPerDay);
  const consultsData = buildSparkData(data.ConsultsPerDay);
  const smsData = buildSparkData(data.SmsPerDay);
  const deliveriesData = buildSparkData(data.DeliveriesPerDay);

  // Initials avatar colour — deterministic from name
  const avatarColor = (() => {
    const palette = ["#3b82f6","#8b5cf6","#ec4899","#10b981","#f59e0b","#06b6d4","#ef4444"];
    const n = (data.ChemistName || "").charCodeAt(0) || 0;
    return palette[n % palette.length];
  })();
  const initials = (data.ChemistName || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();

  return (
    <div className="bg-gray-50 min-w-0 max-w-full" style={{ padding: "6px 16px 12px" }}>

      {/* ── Sticky zone: header card + tab strip ── */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f9fafb", pb: 0 }}>

      {/* ── Header card ─────────────────────────────────────────── */}
      <Box sx={{
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        backgroundColor: "#fff",
        boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
        mb: 1.5,
      }}>
        {/* Top accent bar — contains back button */}
        <Box sx={{
          height: 28,
          background: `linear-gradient(90deg, ${PHARMACY_HALO_COLORS.BLUE} 0%, #6366f1 100%)`,
          display: "flex", alignItems: "center", px: 1,
        }}>
          <Button
            size="small"
            startIcon={<ArrowBackIos sx={{ fontSize: "10px !important" }} />}
            onClick={() => navigate(-1)}
            sx={{
              textTransform: "none", color: "rgba(255,255,255,0.85)", fontWeight: 600,
              fontSize: "0.72rem", py: 0, minWidth: 0,
              "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.12)", borderRadius: 1 },
            }}
          >
            Back
          </Button>
        </Box>

        <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
          {/* ── Row 1: Avatar + name/contact + meta grid ── */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>

            {/* Avatar */}
            <Box sx={{
              width: 52, height: 52, borderRadius: 2, flexShrink: 0,
              backgroundColor: avatarColor, display: "flex", alignItems: "center",
              justifyContent: "center", boxShadow: `0 2px 8px ${avatarColor}55`,
            }}>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.1rem", lineHeight: 1 }}>
                {initials}
              </Typography>
            </Box>

            {/* Name + group + contact */}
            <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", color: "#111827", lineHeight: 1.2 }}>
                  {data.ChemistName}
                </Typography>
                {data.GroupName ? (
                  <Chip
                    label={data.GroupName}
                    size="small"
                    sx={{ backgroundColor: "#e0e7ff", color: "#3730a3", fontWeight: 700, fontSize: "0.7rem", height: 20 }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>No group</Typography>
                )}
              </Box>

              {/* Contact pills row */}
              <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                {data.Email && (
                  <Box
                    component="a" href={`mailto:${data.Email}`}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1.25, py: 0.4, borderRadius: 10,
                      backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
                      color: PHARMACY_HALO_COLORS.BLUE, textDecoration: "none",
                      fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
                      "&:hover": { backgroundColor: "#dbeafe" },
                    }}
                  >
                    <EmailOutlined sx={{ fontSize: 13 }} />
                    {data.Email}
                  </Box>
                )}
                {data.Phone && (
                  <Box
                    component="a" href={`tel:${String(data.Phone).replace(/\D/g, "")}`}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1.25, py: 0.4, borderRadius: 10,
                      backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0",
                      color: "#16a34a", textDecoration: "none",
                      fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
                      "&:hover": { backgroundColor: "#dcfce7" },
                    }}
                  >
                    <PhoneOutlined sx={{ fontSize: 13 }} />
                    {fmtAuPhone(data.Phone)}
                  </Box>
                )}
                {data.SMSDedicatedNumber && (
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 0.5,
                    px: 1.25, py: 0.4, borderRadius: 10,
                    backgroundColor: "#fdf4ff", border: "1px solid #e9d5ff",
                    color: "#7c3aed", fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
                  }}>
                    <SmsOutlined sx={{ fontSize: 13 }} />
                    {fmtAuPhone(data.SMSDedicatedNumber)}
                    <Typography component="span" sx={{ fontSize: "0.65rem", color: "#a78bfa", ml: 0.25 }}>SMS</Typography>
                  </Box>
                )}
              </Box>

              {/* Contact numbers component */}
              {!isManager && (
                <Box sx={{ mt: 0.75 }}>
                  <ChemistContactNumbers chemistUUID={data.ChemistUUID} staffList={data.StaffList} />
                </Box>
              )}
            </Box>

            {/* Meta info grid + Staff button — right side */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0, alignSelf: "flex-start" }}>
              <Box sx={{
                display: "grid", gridTemplateColumns: "repeat(3, auto)", gap: "6px 20px",
              }}>
                {[
                  { label: "ID",        value: data.ChemistID },
                  { label: "Username",  value: data.Username },
                  { label: "TAC",       value: data.TAC },
                  { label: "Dispense",  value: data.DispenseSystemName || dispenseSystemName(data.DispenseType) || "—" },
                  { label: "Installed", value: fmtDate(data.InstallationDate) },
                  {
                    label: "First Staff",
                    value: data.FirstStaff?.Name
                      ? `${data.FirstStaff.Name}${data.FirstStaff?.Login ? ` · ${data.FirstStaff.Login}` : ""}`
                      : (data.FirstStaff?.Login || "—"),
                  },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#9ca3af", lineHeight: 1.2 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", lineHeight: 1.3 }}>
                      {value || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Staff button — quick access to staff list */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<PeopleOutlined sx={{ fontSize: "14px !important" }} />}
                onClick={() => setStaffOpen(true)}
                sx={{
                  textTransform: "none", fontWeight: 700, fontSize: "0.72rem", borderRadius: 2,
                  alignSelf: "flex-start", py: 0.4,
                  borderColor: PHARMACY_HALO_COLORS.BLUE, color: PHARMACY_HALO_COLORS.BLUE,
                  "&:hover": { backgroundColor: "#eff6ff", borderColor: PHARMACY_HALO_COLORS.BLUE },
                }}
              >
                Staff · {activeStaff} active{totalStaff !== activeStaff ? ` / ${totalStaff} total` : ""}
              </Button>
            </Box>
          </Box>

          {/* ── Divider ── */}
          <Box sx={{ height: "1px", backgroundColor: "#f3f4f6", mx: -2.5, mt: 2, mb: 1.5 }} />

          {/* ── Row 2: Primary actions + overflow ── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", pb: 1.5 }}>

            {/* Edit — always visible */}
            {!isManager && (
              <Button
                size="small" variant="contained" startIcon={<EditOutlined sx={{ fontSize: "14px !important" }} />}
                onClick={openEditDrawer}
                sx={{
                  textTransform: "none", fontWeight: 700, fontSize: "0.75rem", borderRadius: 2,
                  backgroundColor: PHARMACY_HALO_COLORS.BLUE, boxShadow: "none",
                  "&:hover": { backgroundColor: "#1d4ed8", boxShadow: "none" },
                }}
              >
                Edit
              </Button>
            )}

            {/* Local Warnings — always visible (has badge) */}
            {!isManager && (() => {
              const activeLabels = getActiveWarningLabels(localWarnings);
              return (
                <Tooltip title={activeLabels.length > 0 ? `Active: ${activeLabels.join(", ")}` : "Per-app local warning banners"}>
                  <Button
                    size="small" variant="outlined"
                    onClick={() => setLocalWarningsOpen(true)}
                    startIcon={<WarningAmberOutlined sx={{ fontSize: "14px !important" }} />}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderRadius: 2,
                      borderColor: activeLabels.length > 0 ? "#d97706" : "#e5e7eb",
                      color: activeLabels.length > 0 ? "#d97706" : "#374151",
                      "&:hover": { backgroundColor: activeLabels.length > 0 ? "#fffbeb" : "#f9fafb", borderColor: activeLabels.length > 0 ? "#b45309" : "#d1d5db" },
                    }}
                  >
                    Warnings
                    {activeLabels.length > 0 && (
                      <Box component="span" sx={{
                        ml: 0.75, display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: 16, height: 16, borderRadius: 10,
                        bgcolor: "#d97706", color: "#fff", fontSize: "0.6rem", fontWeight: 800, lineHeight: 1, px: 0.5,
                      }}>
                        {activeLabels.length}
                      </Box>
                    )}
                  </Button>
                </Tooltip>
              );
            })()}

            {/* ⋯ Overflow menu — secondary actions */}
            {!isManager && (
              <>
                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={(e) => setOverflowAnchor(e.currentTarget)}
                    sx={{ border: "1px solid #e5e7eb", borderRadius: 2, px: 0.75, py: 0.4, color: "#374151", "&:hover": { backgroundColor: "#f9fafb" } }}
                  >
                    <MoreHorizIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={overflowAnchor}
                  open={Boolean(overflowAnchor)}
                  onClose={() => setOverflowAnchor(null)}
                  PaperProps={{ sx: { borderRadius: 2, minWidth: 200, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", border: "1px solid #e5e7eb" } }}
                  transformOrigin={{ horizontal: "left", vertical: "top" }}
                  anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                >
                  {/* Group */}
                  <MenuItem dense onClick={() => { setOverflowAnchor(null); setSelectedGroupID(data.GroupID != null ? String(data.GroupID) : ""); setGroupSaveError(null); setGroupModalOpen(true); }}>
                    <ListItemIcon><PeopleOutlined sx={{ fontSize: 16, color: "#374151" }} /></ListItemIcon>
                    <ListItemText primary="Change group" primaryTypographyProps={{ fontSize: "0.82rem" }} />
                  </MenuItem>

                  {/* Stripe */}
                  {data.StripeCusID && (
                    <MenuItem dense onClick={() => { setOverflowAnchor(null); window.open(`https://dashboard.stripe.com/acct_1FE6RZCsX05mJNe0/customers/${data.StripeCusID}`, "_blank", "noopener,noreferrer"); }}>
                      <ListItemIcon>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z" stroke="#635BFF" strokeWidth="2" fill="none"/>
                          <path d="M9.5 9c0-1.38 1.12-2.5 2.5-2.5 1.06 0 1.96.66 2.33 1.6M9.5 15c0 1.38 1.12 2.5 2.5 2.5 1.38 0 2.5-1.12 2.5-2.5 0-.96-.54-1.8-1.33-2.22L11 9.5" stroke="#635BFF" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </ListItemIcon>
                      <ListItemText primary="Open in Stripe" primaryTypographyProps={{ fontSize: "0.82rem", color: "#635BFF" }} />
                    </MenuItem>
                  )}

                  {/* Calendly */}
                  <MenuItem dense onClick={async () => {
                    setOverflowAnchor(null);
                    setCalendlyModalOpen(true);
                    setGreetingName(data?.ChemistName || "");
                    if (!calendlyEventTypes) {
                      setCalendlyTypesLoading(true);
                      try {
                        const res = await phappsRequest(API_PATHS.CALENDLY_EVENT_TYPES, "GET");
                        setCalendlyEventTypes(res?.event_types || []);
                        if (res?.event_types?.length) setSelectedEventType(res.event_types[0]);
                      } catch { setCalendlyEventTypes([]); }
                      setCalendlyTypesLoading(false);
                    } else if (calendlyEventTypes.length && !selectedEventType) {
                      setSelectedEventType(calendlyEventTypes[0]);
                    }
                  }}>
                    <ListItemIcon>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="18" rx="3" stroke="#006BFF" strokeWidth="2" fill="none"/>
                        <path d="M16 2v4M8 2v4M3 10h18" stroke="#006BFF" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="8" cy="15" r="1.5" fill="#006BFF"/><circle cx="12" cy="15" r="1.5" fill="#006BFF"/><circle cx="16" cy="15" r="1.5" fill="#006BFF"/>
                      </svg>
                    </ListItemIcon>
                    <ListItemText primary="Send booking link" primaryTypographyProps={{ fontSize: "0.82rem" }} />
                  </MenuItem>

                  {/* Calendly — Create Booking */}
                  <MenuItem dense onClick={() => { setOverflowAnchor(null); openCreateBooking(); }}>
                    <ListItemIcon>
                      <EventAvailableOutlined sx={{ fontSize: 16, color: "#059669" }} />
                    </ListItemIcon>
                    <ListItemText primary="Create booking" primaryTypographyProps={{ fontSize: "0.82rem", color: "#059669" }} />
                  </MenuItem>

                  {/* Support app link */}
                  <MenuItem dense disabled={supportAppLinkSending || !data.ChemistID} onClick={async () => {
                    setOverflowAnchor(null);
                    setSupportAppLinkSending(true);
                    try {
                      await phappsRequest(API_PATHS.SPLASHTOP_SEND_SUPPORT_APP_LINK, "POST", { ChemistID: data.ChemistID });
                      setSnackbar({ open: true, message: `Support app link emailed to ${data.Email || "the pharmacy"} ✓`, severity: "success" });
                    } catch (err) {
                      setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || "Failed to send support app link", severity: "error" });
                    } finally { setSupportAppLinkSending(false); }
                  }}>
                    <ListItemIcon><PhoneIphoneOutlined sx={{ fontSize: 16, color: "#0d9488" }} /></ListItemIcon>
                    <ListItemText primary={supportAppLinkSending ? "Sending…" : "Send support app link"} primaryTypographyProps={{ fontSize: "0.82rem", color: "#0d9488" }} />
                  </MenuItem>

                  {/* Remote support */}
                  {data.SplashtopEnabled && (
                    <MenuItem dense disabled={splashtopSending || !data.ChemistID} onClick={async () => {
                      setOverflowAnchor(null);
                      setSplashtopSending(true);
                      try {
                        await phappsRequest(API_PATHS.SPLASHTOP_SEND_SESSION, "POST", { ChemistID: data.ChemistID });
                        setSnackbar({ open: true, message: `Support session emailed to ${data.Email || "the pharmacy"} ✓`, severity: "success" });
                      } catch (err) {
                        setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || "Failed to create support session", severity: "error" });
                      } finally { setSplashtopSending(false); }
                    }}>
                      <ListItemIcon><SupportAgentOutlined sx={{ fontSize: 16, color: "#7c3aed" }} /></ListItemIcon>
                      <ListItemText primary={splashtopSending ? "Sending…" : "Send remote support"} primaryTypographyProps={{ fontSize: "0.82rem", color: "#7c3aed" }} />
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}

            {/* Divider */}
            {!isManager && <Box sx={{ width: "1px", height: 24, backgroundColor: "#e5e7eb", mx: 0.5, flexShrink: 0 }} />}

            {/* Compact toggle switches */}
            {!isManager && (
              <>
                {[
                  {
                    label: "Hotfix", checked: allowHotfix,
                    onChange: async (val) => {
                      setAllowHotfix(val); setFlagsSaving(true);
                      try { await phappsRequest(API_PATHS.ADMIN_UPDATE_CHEMIST_FLAGS, "PUT", { ChemistUUID: data.ChemistUUID, AllowHotfix: val, UseCherub: useCherub }); }
                      catch { setAllowHotfix(!val); } finally { setFlagsSaving(false); }
                    },
                  },
                  {
                    label: "Cherub", checked: useCherub,
                    onChange: async (val) => {
                      setUseCherub(val); setFlagsSaving(true);
                      try { await phappsRequest(API_PATHS.ADMIN_UPDATE_CHEMIST_FLAGS, "PUT", { ChemistUUID: data.ChemistUUID, AllowHotfix: allowHotfix, UseCherub: val }); }
                      catch { setUseCherub(!val); } finally { setFlagsSaving(false); }
                    },
                  },
                  {
                    label: "SMS Reply", checked: allowSMSResponse,
                    onChange: async (val) => {
                      setAllowSMSResponse(val); setFlagsSaving(true);
                      try { await phappsRequest(API_PATHS.ADMIN_UPDATE_CHEMIST_DETAILS, "PATCH", { ChemistUUID: data.ChemistUUID, ChemistName: data.ChemistName, Email: data.Email ?? null, AllowSMSResponse: val }); }
                      catch { setAllowSMSResponse(!val); } finally { setFlagsSaving(false); }
                    },
                  },
                ].map(({ label, checked, onChange }) => (
                  <Box
                    key={label}
                    onClick={() => !flagsSaving && onChange(!checked)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.6,
                      px: 1.25, py: 0.5, borderRadius: 2, cursor: flagsSaving ? "not-allowed" : "pointer",
                      border: `1px solid ${checked ? "#bfdbfe" : "#e5e7eb"}`,
                      backgroundColor: checked ? "#eff6ff" : "#f9fafb",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: checked ? "#93c5fd" : "#d1d5db" },
                    }}
                  >
                    <Box sx={{
                      width: 28, height: 16, borderRadius: 8,
                      backgroundColor: checked ? PHARMACY_HALO_COLORS.BLUE : "#d1d5db",
                      position: "relative", transition: "background 0.2s", flexShrink: 0,
                    }}>
                      <Box sx={{
                        position: "absolute", top: 2, left: checked ? 14 : 2,
                        width: 12, height: 12, borderRadius: "50%",
                        backgroundColor: "#fff", transition: "left 0.2s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: checked ? PHARMACY_HALO_COLORS.BLUE : "#6b7280", whiteSpace: "nowrap" }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* App tab bar */}
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          mb: 0,
          p: 0.5,
          backgroundColor: "#f1f5f9",
          borderRadius: 2,
          overflowX: "auto",
          flexShrink: 0,
        }}
      >
        {visibleTabs.map((tab, i) => {
          const active = activeTab === i;
          const hasSub = tab.subKey === null ? true : Boolean(subs[tab.subKey]);
          const metricVal = tab.metricKey ? Number(data?.Metrics?.[tab.metricKey] ?? 0) : null;

          const bgColor   = active ? tab.color : hasSub ? tab.pastel : "#e8e8e8";
          const textColor = active ? "white"   : hasSub ? tab.color  : "#a0a0a0";
          const hoverBg   = active ? tab.color : hasSub ? `${tab.color}22` : "#dedede";

          return (
            <Box
              key={i}
              component="button"
              onClick={() => setActiveTab(i)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0,
                px: 1.5,
                py: tab.metricKey ? 0.75 : 1.25,
                minWidth: tab.metricKey ? 86 : 72,
                flexShrink: 0,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.18s ease",
                backgroundColor: bgColor,
                color: textColor,
                boxShadow: active ? "0 3px 10px rgba(0,0,0,0.22)" : "none",
                filter: !hasSub && !active ? "grayscale(0.3)" : "none",
                "&:hover": {
                  backgroundColor: hoverBg,
                  color: active ? "white" : tab.color,
                  boxShadow: active ? "0 3px 10px rgba(0,0,0,0.22)" : "0 3px 8px rgba(0,0,0,0.12)",
                  transform: active ? "none" : "translateY(-2px)",
                },
              }}
            >
              {/* Icon + label row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: tab.metricKey ? 0.4 : 0 }}>
                <tab.Icon sx={{ fontSize: 16, color: "inherit", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.73rem", fontWeight: 700, lineHeight: 1, color: "inherit", whiteSpace: "nowrap" }}>
                  {tab.label}
                </Typography>
              </Box>
              {/* Metric */}
              {tab.metricKey && (
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.1, color: "inherit" }}>
                    {loading ? "…" : metricVal?.toLocaleString() ?? "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.58rem", color: "inherit", opacity: 0.8, lineHeight: 1.2, fontWeight: 500, whiteSpace: "nowrap" }}>
                    {tab.metricLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── End sticky zone ── */}
      </Box>

      {/* ── Tab content (scrolls normally) ── */}
      <Box sx={{ pt: 1.5 }}>

      {/* Tab: Overview */}
      {activeTabLabel === "Overview" && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {subs.QudaActive && (
            <MiniBarChart data={ordersData} color={QUDA_COLORS.TEAL_DARK} label="Quda — orders per day (30d)" />
          )}
          {subs.DocLinkActive && (
            <MiniBarChart data={consultsData} color={DOCLINK_COLORS.DARK_PURPLE} label="DocLink — consults per day (30d)" />
          )}
          {subs.HaloTextActive && (
            <MiniBarChart data={smsData} color={PHARMACY_HALO_COLORS.BLUE} label="HaloText — SMS per day (30d)" />
          )}
          {subs.MediDropActive && (
            <MiniBarChart data={deliveriesData} color={MEDIDROP_COLORS.ORANGE} label="MediDrop — deliveries per day (30d)" />
          )}
          {!subs.QudaActive && !subs.DocLinkActive && !subs.HaloTextActive && !subs.MediDropActive && (
            <Box sx={{ gridColumn: "1/-1", py: 4, textAlign: "center" }}>
              <Typography sx={{ color: "text.disabled", fontSize: "0.85rem" }}>No active app subscriptions to display charts for.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Tab: Quda */}
      {activeTabLabel === "Quda" && <ChemistQudaTab chemistUUID={chemistUUID} isManager={isManager} />}

      {/* Tab: HaloPay */}
      {activeTabLabel === "HaloPay" && <ChemistHaloPayTab chemistUUID={chemistUUID} isManager={isManager} />}

      {/* Tab: HaloText */}
      {activeTabLabel === "HaloText" && <ChemistHaloTextTab chemistUUID={chemistUUID} isManager={isManager} />}

      {/* Tab: DocLink */}
      {activeTabLabel === "DocLink" && <ChemistDocLinkTab chemistUUID={chemistUUID} isManager={isManager} />}

      {/* Tab: MediDrop */}
      {activeTabLabel === "MediDrop" && <ChemistMediDropTab chemistUUID={chemistUUID} isManager={isManager} />}

      {/* Tab: Webbit */}
      {activeTabLabel === "Webbit" && <ChemistWebbitTab chemistUUID={chemistUUID} />}

      {/* Tab: Calls */}
      {activeTabLabel === "Calls" && (
        <ChemistCallsTab
          chemistId={data?.ChemistID}
          chemistUUID={data?.ChemistUUID}
          chemistName={data?.chemistName}
        />
      )}

      {/* Tab: Terminals */}
      {activeTabLabel === "Terminals" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {terminalsLoading && (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <Typography color="textSecondary">Loading terminals…</Typography>
            </Box>
          )}
          {!terminalsLoading && (terminalsData?.Terminals ?? []).length === 0 && (
            <Typography color="textSecondary" sx={{ py: 4, textAlign: "center" }}>
              No terminals found for this pharmacy.
            </Typography>
          )}
          {!terminalsLoading && (terminalsData?.Terminals ?? []).map((t) => (
            <Box
              key={t.LicenseID}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem" }}>Computer</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.CompName || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem" }}>Version</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{t.Version || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem" }}>Resolution</Typography>
                <Typography variant="body2">
                  {t.ScreenWidth && t.ScreenHeight ? `${t.ScreenWidth}×${t.ScreenHeight}` : "—"}
                  {t.IsLowRes && (
                    <Chip label="Low-res" size="small" color="warning" variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: "0.6rem" }} />
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem" }}>Last Seen</Typography>
                <Typography variant="body2">{t.LastAccess ? t.LastAccess.slice(0, 10) : "—"}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Tab: Notes */}
      {activeTabLabel === "Notes" && <ChemistCommentsTab chemistId={data?.ChemistID} />}

      {/* ── End tab content ── */}
      </Box>

      {/* Edit Group modal */}
      <Dialog
        open={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: PHARMACY_HALO_COLORS.BLUE }}>
          Change group — {data?.ChemistName}
        </DialogTitle>
        <DialogContent>
          {groupSaveError && (
            <Alert severity="warning" sx={{ mb: 2 }}>{groupSaveError}</Alert>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Group</InputLabel>
            <Select
              label="Group"
              value={selectedGroupID}
              onChange={(e) => setSelectedGroupID(e.target.value)}
            >
              <MenuItem value=""><em>No group</em></MenuItem>
              {(data?.Groups ?? []).map((g) => (
                <MenuItem key={g.GroupID} value={String(g.GroupID)}>
                  {g.GroupName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGroupModalOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={groupSaving}
            onClick={async () => {
              try {
                setGroupSaving(true);
                setGroupSaveError(null);
                const groupID = selectedGroupID === "" ? null : Number(selectedGroupID);
                await phappsRequest(API_PATHS.ADMIN_UPDATE_CHEMIST_GROUP, "POST", {
                  ChemistUUID: data.ChemistUUID,
                  GroupID: groupID,
                });
                const selectedGroup = (data?.Groups ?? []).find((g) => String(g.GroupID) === selectedGroupID);
                setData((prev) => ({
                  ...prev,
                  GroupID: groupID,
                  GroupName: selectedGroup?.GroupName ?? null,
                }));
                setGroupModalOpen(false);
              } catch (e) {
                setGroupSaveError("Failed to update group. Please try again.");
              } finally {
                setGroupSaving(false);
              }
            }}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: PHARMACY_HALO_COLORS.BLUE }}
          >
            {groupSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff modal */}
      <Dialog
        open={staffOpen}
        onClose={() => setStaffOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            fontWeight: 700,
            color: PHARMACY_HALO_COLORS.BLUE,
          }}
        >
          Staff — {data.ChemistName}
          <IconButton size="small" onClick={() => setStaffOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search name or login"
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
              sx={{ width: 220 }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Active only</Typography>}
            />
            <Typography variant="body2" sx={{ color: "text.secondary", ml: "auto" }}>
              {activeStaff} active / {totalStaff} total
            </Typography>
          </Box>

          <Box sx={{ height: Math.min(500, 56 + staffRows.length * 36 + 52), width: "100%" }}>
            <DataGrid
              rows={staffRows}
              columns={staffColumns}
              density="compact"
              disableColumnMenu
              disableRowSelectionOnClick
              hideFooter={staffRows.length <= 25}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              sx={{ ...pharmacyHaloDataGridSx, width: "100%" }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Details Drawer */}
      <Drawer
        anchor="right"
        open={editDrawerOpen}
        onClose={() => !editSaving && setEditDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 0 } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Drawer header */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0,
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: PHARMACY_HALO_COLORS.BLUE }}>
              Edit Chemist Details
            </Typography>
            <IconButton size="small" onClick={() => setEditDrawerOpen(false)} disabled={editSaving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Drawer body */}
          <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2.5 }}>
            <Stack spacing={2.5}>
              <TextField
                label="Pharmacy name"
                fullWidth
                size="small"
                value={editForm.ChemistName}
                onChange={(e) => setEditForm((f) => ({ ...f, ChemistName: e.target.value }))}
              />

              <TextField
                label="Email"
                fullWidth
                size="small"
                type="email"
                value={editForm.Email}
                onChange={(e) => setEditForm((f) => ({ ...f, Email: e.target.value }))}
              />

              {editError && (
                <Alert severity="error" sx={{ py: 0.5 }}>{editError}</Alert>
              )}
            </Stack>
          </Box>

          {/* Drawer footer */}
          <Box sx={{
            px: 2.5, py: 2, borderTop: "1px solid", borderColor: "divider",
            display: "flex", justifyContent: "flex-end", gap: 1.5, flexShrink: 0,
          }}>
            <Button
              onClick={() => setEditDrawerOpen(false)}
              disabled={editSaving}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={editSaving || !editForm.ChemistName.trim()}
              onClick={() => setEditConfirmOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600, backgroundColor: PHARMACY_HALO_COLORS.BLUE }}
            >
              Review & Save
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Edit Details — confirmation dialog */}
      <Dialog
        open={editConfirmOpen}
        onClose={() => !editSaving && setEditConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You're about to update the following for <strong>{data?.ChemistName}</strong>:
          </Typography>
          <Stack spacing={0.75}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Pharmacy name</Typography>
              <Typography variant="body2" fontWeight={600}>{editForm.ChemistName || "—"}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body2" fontWeight={600}>{editForm.Email || "—"}</Typography>
            </Box>
          </Stack>
          {editError && <Alert severity="error" sx={{ mt: 2, py: 0.5 }}>{editError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditConfirmOpen(false)} disabled={editSaving} sx={{ textTransform: "none" }}>
            Back
          </Button>
          <Button
            variant="contained"
            disabled={editSaving}
            onClick={handleEditSave}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: PHARMACY_HALO_COLORS.BLUE }}
          >
            {editSaving ? "Saving…" : "Confirm & Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Calendly Booking Link Modal ──────────────────────────────── */}
      <Dialog
        open={calendlyModalOpen}
        onClose={() => !calendlySending && setCalendlyModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", boxShadow: "0 25px 50px rgba(0,0,0,0.2)" } }}
      >
        {/* Header */}
        <Box sx={{ background: "linear-gradient(135deg, #006BFF 0%, #0047CC 100%)", px: 3, py: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="3" stroke="white" strokeWidth="2"/><path d="M16 2v4M8 2v4M3 10h18" stroke="white" strokeWidth="2" strokeLinecap="round"/><circle cx="8" cy="15" r="1.5" fill="white"/><circle cx="12" cy="15" r="1.5" fill="white"/><circle cx="16" cy="15" r="1.5" fill="white"/></svg>
              </Box>
              <Box>
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.2 }}>Send Booking Link</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.78rem", mt: 0.25 }}>
                  {data?.ChemistName} &nbsp;·&nbsp; {data?.Email}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setCalendlyModalOpen(false)} disabled={calendlySending}
              sx={{ color: "rgba(255,255,255,0.6)", mt: -0.5, "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>

          {/* ── Step 1: Autocomplete event type selector ── */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1 }}>
              1 · Select booking type
            </Typography>

            {calendlyTypesLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 2, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#f9fafb" }}>
                <Box sx={{ width: 18, height: 18, border: "2.5px solid #e5e7eb", borderTopColor: "#006BFF", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                <Typography variant="body2" color="text.secondary">Loading event types from Calendly…</Typography>
              </Box>
            ) : (
              <Autocomplete
                options={calendlyEventTypes || []}
                value={selectedEventType}
                onChange={(_, v) => setSelectedEventType(v)}
                getOptionLabel={(opt) => opt?.name || ""}
                isOptionEqualToValue={(opt, val) => opt?.uri === val?.uri}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search or select a booking type…"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006BFF", borderWidth: 2 },
                      },
                    }}
                  />
                )}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: "10px !important", px: "14px !important" }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0, bgcolor: opt.color || "#006BFF" }} />
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827", flex: 1 }}>
                      {opt.name}
                      {opt.duration && (
                        <Typography component="span" sx={{ ml: 1, fontSize: "0.75rem", fontWeight: 400, color: "#9ca3af" }}>
                          {opt.duration} min
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}
                noOptionsText="No event types found — check Calendly configuration"
                clearOnEscape
                disablePortal={false}
                componentsProps={{ paper: { sx: { borderRadius: 2, boxShadow: "0 10px 30px rgba(0,0,0,0.12)", mt: 0.5 } } }}
              />
            )}

            {/* Selected event detail pill */}
            {selectedEventType && (
              <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, bgcolor: "#eff6ff", borderRadius: 1.5, border: "1px solid #bfdbfe" }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: selectedEventType.color || "#006BFF", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#006BFF", flex: 1 }}>{selectedEventType.name}</Typography>
                {selectedEventType.duration && (
                  <Chip label={`${selectedEventType.duration} min`} size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 700, border: "none" }} />
                )}
              </Box>
            )}
          </Box>

          {/* ── Greeting name override ── */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1 }}>
              Greeting name
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. John"
              value={greetingName}
              onChange={(e) => setGreetingName(e.target.value)}
              helperText={`Defaults to the pharmacy name. Override with a staff member's name if preferred.`}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006BFF", borderWidth: 2 } } }}
            />
          </Box>

          {/* ── Divider ── */}
          <Divider sx={{ mb: 2.5 }} />

          {/* ── Step 2: Email preview ── */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1.5 }}>
              2 · Email preview
            </Typography>

            {!selectedEventType ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3, px: 2, border: "1.5px dashed #e5e7eb", borderRadius: 2, bgcolor: "#fafafa" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 8, opacity: 0.3 }}>
                  <rect x="3" y="4" width="18" height="18" rx="3" stroke="#6b7280" strokeWidth="1.5"/>
                  <path d="M16 2v4M8 2v4M3 10h18" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <Typography variant="body2" color="text.disabled">Select a booking type above to preview the email</Typography>
              </Box>
            ) : (
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2.5, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                {/* Email envelope header */}
                <Box sx={{ bgcolor: "#f3f4f6", px: 2, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#fca5a5" }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#fcd34d" }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#6ee7b7" }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", flex: 1, textAlign: "center", fontWeight: 500 }}>
                    To: {data?.Email}
                  </Typography>
                </Box>
                {/* Email body */}
                <Box sx={{ bgcolor: "#fff" }}>
                  {/* Brand header */}
                  <Box sx={{ bgcolor: "#002169", px: 3, py: 2, textAlign: "center" }}>
                    <Typography sx={{ color: "#FFAF3C", fontSize: "0.62rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase" }}>Pharmacy Halo</Typography>
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", mt: 0.5, lineHeight: 1.3 }}>{selectedEventType.name}</Typography>
                  </Box>
                  {/* Body content */}
                  <Box sx={{ px: 3, py: 2.5 }}>
                    <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.8, mb: 1 }}>
                      Hi <strong>{greetingName || 'there'}</strong>,
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.8, mb: 2 }}>
                      Please use the button below to book your <strong>{selectedEventType.name}</strong> session with the Pharmacy Halo team.
                    </Typography>
                    {/* CTA */}
                    <Box sx={{ textAlign: "center", mb: 2.5 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, bgcolor: "#002169", color: "#fff", borderRadius: 1.5, px: 3, py: 1.25, fontSize: "0.82rem", fontWeight: 700 }}>
                        <span>📅</span> Book Your Session
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2, borderColor: "#f3f4f6" }} />
                    <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.7 }}>
                      Regards,<br />
                      <strong style={{ color: "#002169" }}>Pharmacy Halo Team</strong>
                    </Typography>
                  </Box>
                  {/* Footer */}
                  <Box sx={{ bgcolor: "#f9fafb", borderTop: "1px solid #f3f4f6", px: 3, py: 1.5, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af", lineHeight: 1.5 }}>
                      Sent by Pharmacy Halo · pharmacyhalo.com.au
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <Box sx={{ px: 3, pb: 3, pt: 0, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setCalendlyModalOpen(false)} disabled={calendlySending} sx={{ textTransform: "none", color: "text.secondary", fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedEventType || calendlySending || !data?.Email}
              onClick={async () => {
                if (!selectedEventType || !data?.Email) return;
                setCalendlySending(true);
                try {
                  await phappsRequest(API_PATHS.CALENDLY_SEND_BOOKING_LINK, "POST", {
                    chemist_name:    greetingName || data.ChemistName,
                    to_email:        data.Email,
                    event_type_name: selectedEventType.name,
                    booking_url:     selectedEventType.scheduling_url,
                  });
                  setCalendlyModalOpen(false);
                  setSnackbar({ open: true, message: `Booking link sent to ${data.Email} ✓`, severity: "success" });
                } catch (err) {
                  setSnackbar({ open: true, message: err?.message || "Failed to send booking link", severity: "error" });
                } finally {
                  setCalendlySending(false);
                }
              }}
              sx={{ textTransform: "none", bgcolor: "#006BFF", fontWeight: 700, px: 3, borderRadius: 2,
                "&:hover": { bgcolor: "#0052cc" }, "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" } }}
            >
              {calendlySending ? "Sending…" : "Send Email"}
            </Button>
          </Box>
        </Box>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </Dialog>

      {/* ── Create Booking Modal ──────────────────────────────── */}
      <Dialog
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", boxShadow: "0 25px 50px rgba(0,0,0,0.2)" } }}
      >
        {/* Header */}
        <Box sx={{ background: "linear-gradient(135deg, #059669 0%, #047857 100%)", px: 3, py: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <EventAvailableOutlined sx={{ fontSize: 20, color: "#fff" }} />
              </Box>
              <Box>
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.2 }}>Create Booking</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.78rem", mt: 0.25 }}>
                  {data?.ChemistName}
                  {data?.Email ? ` · ${data.Email}` : ""}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setCreateBookingOpen(false)}
              sx={{ color: "rgba(255,255,255,0.6)", mt: -0.5, "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* Step indicator */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            {[{ n: 1, label: "Event type" }, { n: 2, label: "Date & time" }].map(({ n, label }) => (
              <Box key={n} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: cbStep >= n ? "#fff" : "rgba(255,255,255,0.2)",
                  color: cbStep >= n ? "#059669" : "rgba(255,255,255,0.5)",
                  fontSize: "0.7rem", fontWeight: 800,
                }}>
                  {n}
                </Box>
                <Typography sx={{ color: cbStep >= n ? "#fff" : "rgba(255,255,255,0.5)", fontSize: "0.75rem", fontWeight: cbStep === n ? 700 : 400 }}>
                  {label}
                </Typography>
                {n < 2 && <Box sx={{ width: 20, height: 1.5, bgcolor: "rgba(255,255,255,0.25)", mx: 0.5 }} />}
              </Box>
            ))}
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          {cbStep === 1 ? (
            /* ── Step 1: Select event type ─────────────────────── */
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1.5 }}>
                Select a meeting type
              </Typography>
              {calendlyTypesLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 2, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#f9fafb" }}>
                  <Box sx={{ width: 18, height: 18, border: "2.5px solid #e5e7eb", borderTopColor: "#059669", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  <Typography variant="body2" color="text.secondary">Loading event types…</Typography>
                </Box>
              ) : (
                <Autocomplete
                  options={calendlyEventTypes || []}
                  value={cbSelectedType}
                  onChange={(_, v) => setCbSelectedType(v)}
                  getOptionLabel={(opt) => opt?.name || ""}
                  isOptionEqualToValue={(opt, val) => opt?.uri === val?.uri}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Search or select a meeting type…" size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#059669", borderWidth: 2 } } }}
                    />
                  )}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: "10px !important", px: "14px !important" }}>
                      <Box sx={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0, bgcolor: opt.color || "#059669" }} />
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827", flex: 1 }}>
                        {opt.name}
                        {opt.duration && (
                          <Typography component="span" sx={{ ml: 1, fontSize: "0.75rem", fontWeight: 400, color: "#9ca3af" }}>{opt.duration} min</Typography>
                        )}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText="No event types found — check Calendly configuration"
                  clearOnEscape
                  componentsProps={{ paper: { sx: { borderRadius: 2, boxShadow: "0 10px 30px rgba(0,0,0,0.12)", mt: 0.5 } } }}
                />
              )}
              {cbSelectedType && (
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, bgcolor: "#f0fdf4", borderRadius: 1.5, border: "1px solid #bbf7d0" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cbSelectedType.color || "#059669", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#059669", flex: 1 }}>{cbSelectedType.name}</Typography>
                  {cbSelectedType.duration && (
                    <Chip label={`${cbSelectedType.duration} min`} size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700, border: "none" }} />
                  )}
                </Box>
              )}
            </Box>
          ) : (
            /* ── Step 2: Date & time selection ─────────────────── */
            <Box>
              {/* Date strip */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                <IconButton size="small" disabled={cbWeekOffset <= 0} onClick={() => setCbWeekOffset((w) => w - 1)}
                  sx={{ color: cbWeekOffset <= 0 ? "#d1d5db" : "#374151" }}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Box sx={{ display: "flex", gap: 0.75, flex: 1, overflowX: "auto", pb: 0.5 }}>
                  {getStripDates(cbWeekOffset).map((dateStr) => {
                    const { dayName, dayNum, monthName } = parseDateParts(dateStr);
                    const isSelected = cbSelectedDate === dateStr;
                    const today = getPerthTodayStr();
                    const isToday = dateStr === today;
                    return (
                      <Button key={dateStr} onClick={() => {
                        setCbSelectedDate(dateStr);
                        fetchCbTimes(cbSelectedType.uri, dateStr);
                      }}
                        sx={{
                          minWidth: 52, flexDirection: "column", borderRadius: 2, px: 0.5, py: 0.75, flexShrink: 0,
                          bgcolor: isSelected ? "#059669" : isToday ? "#f0fdf4" : "#f9fafb",
                          border: isSelected ? "1.5px solid #059669" : isToday ? "1.5px solid #86efac" : "1.5px solid #e5e7eb",
                          "&:hover": { bgcolor: isSelected ? "#047857" : "#f0fdf4", borderColor: "#059669" },
                        }}
                      >
                        <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: isSelected ? "rgba(255,255,255,0.8)" : "#9ca3af", lineHeight: 1 }}>{dayName}</Typography>
                        <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: isSelected ? "#fff" : "#111827", lineHeight: 1.2, mt: 0.25 }}>{dayNum}</Typography>
                        <Typography sx={{ fontSize: "0.6rem", color: isSelected ? "rgba(255,255,255,0.7)" : "#9ca3af", lineHeight: 1, mt: 0.1 }}>{monthName}</Typography>
                      </Button>
                    );
                  })}
                </Box>
                <IconButton size="small" onClick={() => setCbWeekOffset((w) => w + 1)}
                  sx={{ color: "#374151" }}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Time slots */}
              {!cbSelectedDate ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, border: "1.5px dashed #e5e7eb", borderRadius: 2, bgcolor: "#fafafa" }}>
                  <Typography variant="body2" color="text.disabled">Select a date above to see available times</Typography>
                </Box>
              ) : cbTimesLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4, gap: 1.5 }}>
                  <Box sx={{ width: 18, height: 18, border: "2.5px solid #e5e7eb", borderTopColor: "#059669", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  <Typography variant="body2" color="text.secondary">Checking availability…</Typography>
                </Box>
              ) : !cbAvailableTimes?.length ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, border: "1.5px dashed #e5e7eb", borderRadius: 2, bgcolor: "#fafafa" }}>
                  <Typography variant="body2" color="text.disabled">No available times on this date</Typography>
                </Box>
              ) : (
                <Box>
                  {[
                    { label: "🌅 Morning", min: 0, max: 12 },
                    { label: "☀️ Afternoon", min: 12, max: 17 },
                    { label: "🌆 Evening", min: 17, max: 24 },
                  ].map(({ label, min, max }) => {
                    const slots = cbAvailableTimes.filter((s) => {
                      const h = perthHour(s.start_time);
                      return h >= min && h < max;
                    });
                    if (!slots.length) return null;
                    return (
                      <Box key={label} sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
                          {label}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {slots.map((slot) => {
                            const isSelected = cbSelectedSlot?.start_time === slot.start_time;
                            return (
                              <Button key={slot.start_time} size="small" onClick={() => setCbSelectedSlot(slot)}
                                sx={{
                                  borderRadius: 2, px: 1.5, py: 0.5, minWidth: 0,
                                  bgcolor: isSelected ? "#059669" : "#f9fafb",
                                  border: isSelected ? "1.5px solid #059669" : "1.5px solid #e5e7eb",
                                  color: isSelected ? "#fff" : "#374151",
                                  fontWeight: isSelected ? 700 : 500,
                                  fontSize: "0.8rem",
                                  textTransform: "none",
                                  "&:hover": { bgcolor: isSelected ? "#047857" : "#f0fdf4", borderColor: "#059669" },
                                }}
                              >
                                {fmtSlotTimePth(slot.start_time)}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Selected slot confirmation */}
                  {cbSelectedSlot && (
                    <Box sx={{ mt: 1, px: 1.5, py: 1.25, bgcolor: "#f0fdf4", border: "1px solid #86efac", borderRadius: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                      <EventAvailableOutlined sx={{ fontSize: 16, color: "#059669", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.82rem", color: "#15803d", fontWeight: 600 }}>
                        {cbSelectedType?.name} · {fmtSlotTimePth(cbSelectedSlot.start_time)} — {(() => {
                          const { dayName, dayNum, monthName } = parseDateParts(cbSelectedDate);
                          return `${dayName} ${dayNum} ${monthName}`;
                        })()}
                      </Typography>
                      {data?.Email && (
                        <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", ml: "auto" }}>→ {data.Email}</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {/* Footer actions */}
        <Box sx={{ px: 3, pb: 3, pt: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            {cbStep === 2 && (
              <Button onClick={() => setCbStep(1)} sx={{ textTransform: "none", color: "text.secondary", fontWeight: 600, fontSize: "0.85rem" }}>
                ← Back
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setCreateBookingOpen(false)} sx={{ textTransform: "none", color: "text.secondary", fontWeight: 600 }}>
              Cancel
            </Button>
            {cbStep === 1 ? (
              <Button
                variant="contained"
                disabled={!cbSelectedType}
                onClick={() => {
                  setCbStep(2);
                  const today = getPerthTodayStr();
                  setCbSelectedDate(today);
                  fetchCbTimes(cbSelectedType.uri, today);
                }}
                sx={{ textTransform: "none", bgcolor: "#059669", fontWeight: 700, px: 3, borderRadius: 2, "&:hover": { bgcolor: "#047857" }, "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" } }}
              >
                Next →
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={!cbSelectedSlot}
                onClick={() => {
                  try {
                    const url = new URL(cbSelectedSlot.scheduling_url);
                    if (data?.ChemistName) url.searchParams.set("name", data.ChemistName);
                    if (data?.Email) url.searchParams.set("email", data.Email);
                    window.open(url.toString(), "_blank", "noopener,noreferrer");
                  } catch {
                    window.open(cbSelectedSlot.scheduling_url, "_blank", "noopener,noreferrer");
                  }
                  setCreateBookingOpen(false);
                  setSnackbar({ open: true, message: "Booking page opened in a new tab ↗", severity: "success" });
                }}
                sx={{ textTransform: "none", bgcolor: "#059669", fontWeight: 700, px: 3, borderRadius: 2, "&:hover": { bgcolor: "#047857" }, "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" } }}
              >
                Open Booking Page ↗
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Local Warnings modal */}
      {!isManager && (
        <LocalWarningsModal
          isOpen={localWarningsOpen}
          onClose={() => setLocalWarningsOpen(false)}
          chemistID={data?.ChemistID}
          chemistName={data?.ChemistName}
          onSaved={(warnings) => setLocalWarnings(warnings)}
        />
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminChemistPage;
